// import: styles
// import: assets
import { ReactComponent as Alert } from 'core/assets/svg/fixed-toast/alert.svg';
import { ReactComponent as Check } from 'core/assets/svg/fixed-toast/check.svg';
import { ReactComponent as Refresh } from 'core/assets/svg/fixed-toast/refresh.svg';
import {
	MAX_AUDIO_DURATION,
	MAX_AUDIO_SIZE,
	MAX_LIP_SYNC_GENERATIONS_ALLOWED,
	MAX_VIDEO_FRAMES,
	MAX_VIDEO_SIZE,
} from 'core/constants/constants';

// import: constants
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import React from 'react';

// import: utils
// import: services
// import: redux
// import: components
// import: lazy load components

export const toastConfig = {
	[ToastType.DEFAULT_SOMETHING_WENT_WRONG_TRY_AGAIN]: {
		icon: <Alert />,
		title: 'Something went wrong. Please try again',
	},

	[ToastType.REFRESH_ON_API_FAILURE]: { icon: <Refresh />, title: 'Please refresh the website' },

	[ToastType.UPLOAD_IMAGE_ERROR]: {
		icon: <Alert />,
		title: 'Image upload failed. Please try again',
	},

	[ToastType.GENERATE_API_ERROR]: {
		icon: <Alert />,
		title: 'Something went wrong. Please try again',
	},

	[ToastType.NO_MODEL_SELECTED]: { icon: <Alert />, title: 'Please select a model' },
	[ToastType.GENERATE_STUDIO_IMAGE_API_SUCCESS]: {
		icon: <Check />,
		title: 'Your image is generated',
	},
	[ToastType.GENERATE_STUDIO_IMAGE_LOW_BALANCE_ERROR]: {
		icon: <Alert />,
		title: 'Image generation failed due to insufficient USDbC balance',
	},
	[ToastType.GENERATE_STUDIO_IMAGE_LOW_ALLOWANCE_ERROR]: {
		icon: <Alert />,
		title: 'Image generation failed due to insufficient USDbC spending cap',
	},

	[ToastType.LINK_COPIED]: { icon: <Check />, title: 'Image link copied' },
	[ToastType.VIDEO_LINK_COPIED]: { icon: <Check />, title: 'Video link copied' },
	[ToastType.PROMPT_LINK_COPIED]: { icon: <Check />, title: 'Prompt link copied' },

	[ToastType.VALUE_COPIED]: { icon: <Check />, title: 'Copied' },

	[ToastType.PAGE_LINK_COPIED]: { icon: <Check />, title: 'Page link copied' },
	[ToastType.PROMPT_UNPUBLISHED]: { icon: <Check />, title: 'Prompt unpublished' },
	[ToastType.GET_CREDIT_OPTIONS_FAILED]: { icon: <Alert />, title: 'Failed to get credit options' },
	[ToastType.UPDATE_PROFILE_SUCCESS]: { icon: <Check />, title: 'Profile successfully updated' },

	[ToastType.FILE_SIZE_TOO_LARGE]: { icon: <Alert />, title: 'Image size can’t exceed 5 MB' },
	[ToastType.INCOMPATIBLE_FILE_TYPE]: {
		icon: <Alert />,
		title: 'Incompatible file. Please upload a .jpg or .png file',
	},
	[ToastType.IMAGE_DIMENSIONS_TOO_LARGE]: {
		icon: <Alert />,
		title: 'Image dimensions can’t exceed 3000x3000 pixels',
	},
	[ToastType.INCOMPATIBLE_VIDEO_FILE_TYPE]: {
		icon: <Alert />,
		title: 'Incompatible file. Please upload a .mp4 or .webm file',
	},
	[ToastType.VIDEO_FILE_SIZE_TOO_LARGE]: {
		icon: <Alert />,
		title: `Video size can’t exceed ${MAX_VIDEO_SIZE / (1024 * 1024)} MB`,
	},
	[ToastType.VIDEO_FRAMES_LIMIT_REACHED]: {
		icon: <Alert />,
		title: `Video Frame limit reached. Please upload a video with less than ${MAX_VIDEO_FRAMES} frames`,
	},
	[ToastType.AUDIO_FILE_SIZE_TOO_LARGE]: {
		icon: <Alert />,
		title: `Audio size can’t exceed ${MAX_AUDIO_SIZE / (1024 * 1024)} MB`,
	},
	[ToastType.AUDIO_DURATION_TOO_LONG]: {
		icon: <Alert />,
		title: `Audio duration can’t exceed ${MAX_AUDIO_DURATION / 60} minutes`,
	},
	[ToastType.INCOMPATIBLE_AUDIO_FILE_TYPE]: {
		icon: <Alert />,
		title: 'Incompatible file. Please upload a .mp3 file',
	},
	[ToastType.FAILED_TO_GET_VIDEO_GENERATION_COSTS]: {
		icon: <Alert />,
		title: 'Failed to get video generation costs',
	},

	[ToastType.FAILED_TO_GET_ENHANCE_COSTS]: {
		icon: <Alert />,
		title: 'Failed to get enhance costs',
	},
	[ToastType.FAILED_TO_GET_ENHANCE_CAP]: { icon: <Alert />, title: 'Failed to get enhance cap' },
	[ToastType.FAILED_TO_GET_COST]: { icon: <Alert />, title: 'Failed to get cost' },

	[ToastType.CUSTOM]: { icon: <Alert />, title: 'Something went wrong.' },

	[ToastType.EMPTY_USERNAME]: { icon: <Alert />, title: 'Username cannot be empty' },
	[ToastType.PLEASE_VERIFY_EMAIL]: {
		icon: <Alert />,
		title: 'Please verify your email before logging in.',
	},
	[ToastType.RESET_PASSWORD_LINK_SENT]: {
		icon: <Check />,
		title: 'We have emailed you a password reset link.',
	},
	[ToastType.FAILED_TO_SAVE_NAME]: {
		icon: <Alert />,
		title: 'Failed to save name. Please update the name in edit profile section.',
	},
	[ToastType.FAILED_TO_SEND_VERIFICATION_EMAIL]: {
		icon: <Alert />,
		title: 'Failed to send verification email. Please try again!',
	},
	[ToastType.EMAIL_ALREADY_IN_USE]: {
		icon: <Alert />,
		title: 'This email address is already in use.',
	},
	[ToastType.DEFAULT_EMAIL_NOT_VERIFIED]: {
		icon: <Alert />,
		title: 'Please verify your email and then log in to use Affogato.',
	},
	[ToastType.OUT_OF_CREDITS]: {
		icon: <Alert />,
		title: 'You’re out of credits! Subscribe or upgrade your plan to keep generating.',
	},
	[ToastType.NSFW_CONTENT]: {
		icon: <Alert />,
		title:
			'It seems like you’re trying to generate a very explicit image. Please edit your prompt and try again',
	},
	[ToastType.CANVAS_ID_NOT_FOUND]: { icon: <Alert />, title: 'Canvas id not found' },
	[ToastType.CANVAS_LIMIT_REACHED]: { icon: <Alert />, title: 'You can only create 50 canvases.' },

	[ToastType.DELETE_ASSET_ERROR]: { icon: <Alert />, title: 'Asset was not deleted.' },
	[ToastType.ASSET_FETCH_ERROR]: { icon: <Alert />, title: 'Failed to fetch assets.' },
	[ToastType.UPLOAD_ASSET_ERROR]: { icon: <Alert />, title: 'Failed to upload asset.' },
	[ToastType.CANNOT_SELECT_ASSET_ERROR]: { icon: <Alert />, title: 'Cannot select this asset' },

	[ToastType.CH01]: { icon: <Alert />, title: 'Character name already exists.' },

	[ToastType.DOWNLOAD_SUCCESS]: { icon: <Check />, title: 'Download successful' },
	[ToastType.DOWNLOAD_ERROR]: { icon: <Alert />, title: 'Download failed' },
	[ToastType.DOWNLOAD_PENDING]: { icon: <Refresh />, title: 'Download pending' },

	[ToastType.LIP_SYNC_VOICES_LIST_FETCH_ERROR]: {
		icon: <Alert />,
		title: 'Failed to fetch voices list',
	},
	[ToastType.LIP_SYNC_VIDEO_GENERATION_ERROR]: {
		icon: <Alert />,
		title: 'Failed to generate Narrator video',
	},
	[ToastType.TOO_MANY_LIPSYNC_GENERATIONS]: {
		icon: <Alert />,
		title: `You can only generate ${MAX_LIP_SYNC_GENERATIONS_ALLOWED} narrator video at a time`,
	},

	[ToastType.FAILED_TO_APPLY_COUPON]: { icon: <Alert />, title: 'Failed to apply coupon code' },
	[ToastType.NSFW_ASSET_IS_NOT_ALLOWED]: {
		icon: <Alert />,
		title: 'NSFW content detected. Please only upload images & videos without explicit content',
	},
	[ToastType.UPGRADE_TO_USE_MAGIC_MODE_AND_PRO_MODE]: {
		title: 'Upgrade to use Magic Mode and Pro Mode',
	},
	[ToastType.GEN_FAILED_DUE_TO_CONTENT_MODERATION]: {
		icon: <Alert />,
		title: 'Generation blocked. Please try changing your prompt or source image.',
	},
	[ToastType.VIDEO_GENERATION_FAILED]: {
		icon: <Alert />,
		title: 'Video generation failed. Your credits have been refunded. Please try again.',
	},
	[ToastType.NSFW_CHARACTER_PROMPT_DESCRIPTION]: {
		icon: <Alert />,
		title:
			'Your character description contains explicit words. Please remove them to save your character.',
	},
};
